import { Box, Button, IconButton, Tooltip, useTheme } from '@mui/material'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { toast } from 'react-toastify'
import { loading } from '../appointments/models'
import { ColDef, INumberFilterParams } from 'ag-grid-community'
import useCustomStyles from '../../utils/hooks/CustomStylesHook'
import CustomHeader from '../Radiology/components/RadiologyModel/CustomHeader'
import MedAgDataGrid from '../../components/MedAgDataGrid'
import { pageSizeOptions } from '../../utils/constants'
import EditIcon from '@mui/icons-material/Edit'
import GetAppOutlinedIcon from '@mui/icons-material/GetAppOutlined'
import ReferralForm from './ReferralForm'
import { emptyString } from '../Radiology/Constants/constants'
import { makeName } from '../lms/utils'
import ProvenanceView from '../../components/ProvenanceView'
import GlobalSearch from '../../components/GlobalSearch'
import ReferralExportDialog from './ReferralExportDialog'
import {
  ADD_REFERRAL,
  AG_FILTER_PARAMS,
  BTN_UPLOAD,
  CELL_STYLE_ACTION,
  CELL_STYLE_CENTER,
  CELL_STYLE_DEFAULT,
  CELL_STYLE_PROVENANCE,
  COL_HEADER_ACTION,
  COL_HEADER_CREATED_BY,
  COL_HEADER_DEPARTMENT,
  COL_HEADER_MOBILE_NO,
  COL_HEADER_NAME,
  COL_HEADER_REASON,
  COL_HEADER_REFERRAL_DATE,
  COL_HEADER_REFERRED_DOCTOR,
  COL_HEADER_REFERRED_HOSPITAL,
  COL_HEADER_REFERRER_NAME,
  COL_HEADER_SERVICE,
  COL_HEADER_SL_NO,
  COL_HEADER_SOURCE,
  COL_HEADER_UHID,
  COL_HEADER_UPDATED_BY,
  EDIT_REFERRAL_TOOLTIP,
  MOBILE,
  PATIENT_MOBILE,
  PROVENANCE_MODE_CREATE,
  PROVENANCE_MODE_UPDATE,
  PROVENANCE_UNKNOWN,
  REFERRAL_DETAILS_TITLE,
  REFERRAL_SEARCH_OPTIONS,
  TOAST_UPLOAD_FAILED,
  TOAST_UPLOAD_INVALID_FILE,
  TOAST_UPLOAD_SUCCESS_FALLBACK,
} from './constants'
import {
  fetchReferrals,
  filterReferrals,
  fetchPatientsByMobile,
  fetchPatientsByFhirIds,
  filterReferralsWithoutMobile,
  uploadReferralDetails,
} from '../../services/referralService'
import { readableDateTimeFormat } from '../../utils/dateUtils'
import { useAppSelector } from '../../app/hooks'
import {
  selectLocations,
  selectSelectedLocationId,
} from '../location/locationSlice'
import MaskedNumber from '../../components/MaskedNumber'

const styles = () => ({
  centerHeader: {
    color: '#ffffff',
    textAlign: 'center',
    '& .ag-header-cell-text': {
      flex: 1,
      display: 'flex',
      justifyContent: 'center',
    },
  },
  addReferralButton: {
    textTransform: 'none',
  },
  exportButton: {
    textTransform: 'none',
  },
  editIcon: {
    color: '#625e5e',
  },
  hiddenFileInput: {
    display: 'none !important',
  },
})

const ReferralDetails = () => {
  const theme = useTheme()
  const classes = useCustomStyles(styles, theme)
  const [referralDetails, setReferralDetails] = useState<any>([])
  const [filterKey, setFilterKey] = useState<{
    key: string | null
    value: string | null
  }>({
    key: null,
    value: null,
  })
  const [openDialog, setOpenDialog] = useState(false)
  const [editReferral, setEditReferral] = useState<any | null>(null)
  const [openExportDialog, setOpenExportDialog] = useState(false)
  const [uploadingFile, setUploadingFile] = useState(false)
  const [status, setStatus] = useState('')
  const [page, setPage] = useState(0)
  const [pageSize, setPageSize] = useState(100)
  const [totalCount, setTotalCount] = useState(0)
  const locationId = useAppSelector(selectSelectedLocationId)
  const locations = useAppSelector(selectLocations)
  const fileInputRef = useRef<HTMLInputElement | null>(null)

  const locationOptions = useMemo(
    () =>
      Array.from(
        new Set(
          (locations ?? [])
            ?.map((loc) => loc?.resource?.name?.trim())
            ?.filter((name): name is string => Boolean(name))
        )
      ),
    [locations]
  )

  const response = useCallback(async () => {
    setStatus(loading)
    try {
      let data: any
      let patientEntries: any[] = []
      if (!filterKey?.key || !filterKey?.value) {
        data = await fetchReferrals(pageSize, page)
      } else if (filterKey?.key === PATIENT_MOBILE) {
        patientEntries = await fetchPatientsByMobile(
          filterKey?.value,
          locationId
        )

        const patientIds = Array.from(
          new Set(
            patientEntries
              ?.map((entry: any) => entry?.resource?.id)
              ?.filter(Boolean)
          )
        )

        if (!patientIds?.length) {
          setReferralDetails([])
          setTotalCount(0)
          return
        }

        data = await filterReferrals(
          pageSize,
          page,
          'patient_fhir_id',
          patientIds
        )
      } else {
        data = await filterReferralsWithoutMobile(
          pageSize,
          page,
          filterKey?.key,
          filterKey?.value
        )
      }

      const referrals =
        !filterKey?.key || !filterKey?.value
          ? data?.referrer_details
          : data?.data?.referrer_details
      if (!referrals?.length) {
        setReferralDetails([])
        return
      }

      // Collect unique FHIR IDs from all referral records
      const fhirIds = Array.from(
        new Set(referrals?.map((r: any) => r?.patient_fhir_id)?.filter(Boolean))
      )?.join(',')

      // Batch-fetch patient data from FHIR
      const entries = patientEntries?.length
        ? patientEntries
        : await fetchPatientsByFhirIds(fhirIds)

      // Build lookup map: fhir_id -> entry
      const patientMap = entries?.reduce(
        (acc: any, cur: any) => ({
          ...acc,
          [cur?.resource?.id]: cur,
        }),
        {}
      )
      // Merge each referral row with its FHIR patient data
      const mapped = referrals?.map((r: any) => {
        const pat = patientMap[r?.patient_fhir_id] ?? null
        return {
          ...r,
          patient_name: makeName(pat?.resource?.name) || r?.patient_name,
          patient_mobile:
            pat?.resource?.telecom?.find((t: any) => t?.use === MOBILE)
              ?.value ?? '',
        }
      })
      setReferralDetails(mapped)
      const count =
        !filterKey?.key || !filterKey?.value
          ? data?.referrer_details_aggregate?.aggregate?.count
          : data?.data?.referrer_details_aggregate?.aggregate?.count || 0
      setTotalCount(count)
    } catch (error) {
      setReferralDetails([])
      setTotalCount(0)
    } finally {
      setStatus('')
    }
  }, [pageSize, page, filterKey, locationId])

  useEffect(() => {
    response()
  }, [response])

  const columnDefs: ColDef[] = [
    {
      headerName: COL_HEADER_SL_NO,
      field: 'slNo',
      minWidth: 80,
      sortable: true,
      filter: false,
      headerClass: classes?.centerHeader,
      cellClass: 'custom-header-cell',
      cellStyle: CELL_STYLE_CENTER,
      cellRenderer: (params: any) => params?.node?.rowIndex + 1,
    },
    {
      headerName: COL_HEADER_UHID,
      field: 'id',
      flex: 1,
      minWidth: 120,
      sortable: true,
      filter: true,
      cellRenderer: (params: any) => params?.data?.patient_uhid,
      headerComponent: CustomHeader,
      cellClass: 'custom-header-cell',
      headerClass: 'custom-header-cell',
      cellStyle: CELL_STYLE_DEFAULT,
      filterParams: AG_FILTER_PARAMS as INumberFilterParams,
    },
    {
      headerName: COL_HEADER_NAME,
      field: 'name',
      minWidth: 300,
      sortable: true,
      filter: true,
      cellRenderer: (params: any) => params?.data?.patient_name,
      headerComponent: CustomHeader,
      cellClass: 'custom-header-cell',
      headerClass: 'custom-header-cell',
      cellStyle: {
        fontSize: '12px',
        textAlign: 'left',
        wordBreak: 'break-word',
        overflowWrap: 'break-word',
        lineHeight: 1,
        display: 'flex',
        alignItems: 'center',
      },
      wrapText: true,
      autoHeight: true,
      filterParams: AG_FILTER_PARAMS as INumberFilterParams,
    },
    {
      headerName: COL_HEADER_MOBILE_NO,
      field: 'mobile',
      minWidth: 140,
      sortable: true,
      filter: true,
      cellRenderer: (params: any) =>
        params?.data?.patient_mobile ? (
          <MaskedNumber mobile={params?.data?.patient_mobile} />
        ) : (
          ''
        ),
      headerComponent: CustomHeader,
      cellClass: 'custom-header-cell',
      headerClass: 'custom-header-cell',
      cellStyle: CELL_STYLE_CENTER,
      filterParams: AG_FILTER_PARAMS as INumberFilterParams,
    },
    {
      headerName: COL_HEADER_SERVICE,
      field: 'service',
      minWidth: 140,
      sortable: true,
      filter: true,
      cellRenderer: (params: any) => params?.data?.service,
      headerComponent: CustomHeader,
      cellClass: 'custom-header-cell',
      headerClass: 'custom-header-cell',
      cellStyle: CELL_STYLE_CENTER,
      filterParams: AG_FILTER_PARAMS as INumberFilterParams,
    },
    {
      headerName: COL_HEADER_DEPARTMENT,
      field: 'department',
      minWidth: 140,
      sortable: true,
      filter: true,
      cellRenderer: (params: any) => params?.data?.department,
      headerComponent: CustomHeader,
      cellClass: 'custom-header-cell',
      headerClass: 'custom-header-cell',
      cellStyle: CELL_STYLE_CENTER,
      filterParams: AG_FILTER_PARAMS as INumberFilterParams,
    },
    {
      headerName: COL_HEADER_SOURCE,
      field: 'source',
      minWidth: 110,
      sortable: true,
      filter: true,
      cellRenderer: (params: any) => params?.data?.source ?? null,
      headerComponent: CustomHeader,
      cellClass: 'custom-header-cell',
      headerClass: 'custom-header-cell',
      cellStyle: CELL_STYLE_PROVENANCE,
      autoHeight: true,
      wrapText: true,
      filterParams: AG_FILTER_PARAMS as INumberFilterParams,
    },
    {
      headerName: COL_HEADER_REFERRED_HOSPITAL,
      field: 'hospital',
      minWidth: 200,
      sortable: true,
      filter: true,
      cellRenderer: (params: any) => params?.data?.hospital,
      headerComponent: CustomHeader,
      cellClass: 'custom-header-cell',
      headerClass: 'custom-header-cell',
      cellStyle: {
        fontSize: '12px',
        textAlign: 'left',
        wordBreak: 'break-word',
        overflowWrap: 'break-word',
        lineHeight: 1,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'flex-start',
      },
      wrapText: true,
      autoHeight: true,
      filterParams: AG_FILTER_PARAMS as INumberFilterParams,
    },
    {
      headerName: COL_HEADER_REFERRER_NAME,
      field: 'referrerName',
      minWidth: 140,
      sortable: true,
      filter: true,
      cellRenderer: (params: any) => params?.data?.referred_to ?? null,
      headerComponent: CustomHeader,
      cellClass: 'custom-header-cell',
      headerClass: 'custom-header-cell',
      cellStyle: CELL_STYLE_PROVENANCE,
      wrapText: true,
      autoHeight: true,
      filterParams: AG_FILTER_PARAMS as INumberFilterParams,
    },
    {
      headerName: COL_HEADER_REFERRED_DOCTOR,
      field: 'referredDoctor',
      minWidth: 140,
      sortable: true,
      filter: true,
      cellRenderer: (params: any) => params?.data?.referred_from ?? null,
      headerComponent: CustomHeader,
      cellClass: 'custom-header-cell',
      headerClass: 'custom-header-cell',
      cellStyle: CELL_STYLE_PROVENANCE,
      wrapText: true,
      autoHeight: true,
      filterParams: AG_FILTER_PARAMS as INumberFilterParams,
    },
    {
      headerName: COL_HEADER_REASON,
      field: 'notes',
      minWidth: 290,
      sortable: true,
      filter: true,
      cellRenderer: (params: any) => params?.data?.notes ?? null,
      headerComponent: CustomHeader,
      cellClass: 'custom-header-cell',
      headerClass: 'custom-header-cell',
      cellStyle: {
        fontSize: '12px',
        textAlign: 'left',
        wordBreak: 'break-word',
        overflowWrap: 'break-word',
        lineHeight: 1,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'flex-start',
      },
      filterParams: AG_FILTER_PARAMS as INumberFilterParams,
      autoHeight: true,
      wrapText: true,
    },
    {
      headerName: COL_HEADER_REFERRAL_DATE,
      field: 'referralDate',
      minWidth: 180,
      sortable: true,
      filter: true,
      cellRenderer: (params: any) =>
        readableDateTimeFormat(
          params?.data?.referral_date
            ? new Date(params.data.referral_date)
            : new Date()
        ),
      headerComponent: CustomHeader,
      cellClass: 'custom-header-cell',
      headerClass: 'custom-header-cell',
      cellStyle: CELL_STYLE_CENTER,
      filterParams: AG_FILTER_PARAMS as INumberFilterParams,
    },
    {
      headerName: COL_HEADER_CREATED_BY,
      field: 'createdBy',
      minWidth: 160,
      sortable: true,
      filter: true,
      headerComponent: CustomHeader,
      cellRenderer: (params: any) => (
        <ProvenanceView
          mode={PROVENANCE_MODE_CREATE}
          name={params?.data?.created_by ?? PROVENANCE_UNKNOWN}
          date={params?.data?.created_on ?? ''}
        />
      ),
      cellClass: 'custom-header-cell',
      headerClass: 'custom-header-cell',
      cellStyle: CELL_STYLE_PROVENANCE,
      wrapText: true,
      autoHeight: true,
      filterParams: AG_FILTER_PARAMS as INumberFilterParams,
    },
    {
      headerName: COL_HEADER_UPDATED_BY,
      field: 'updatedBy',
      minWidth: 160,
      sortable: true,
      filter: true,
      headerComponent: CustomHeader,
      cellRenderer: (params: any) => (
        <ProvenanceView
          mode={PROVENANCE_MODE_UPDATE}
          name={params?.data?.updated_by ?? ''}
          date={params?.data?.updated_on ?? ''}
        />
      ),
      cellClass: 'custom-header-cell',
      headerClass: 'custom-header-cell',
      cellStyle: CELL_STYLE_PROVENANCE,
      wrapText: true,
      autoHeight: true,
      filterParams: AG_FILTER_PARAMS as INumberFilterParams,
    },
    {
      headerName: COL_HEADER_ACTION,
      field: 'actions',
      minWidth: 100,
      sortable: false,
      filter: false,
      headerClass: classes?.centerHeader,
      cellClass: 'custom-header-cell',
      cellStyle: CELL_STYLE_ACTION,
      cellRenderer: (params: any) => (
        <Tooltip title={EDIT_REFERRAL_TOOLTIP}>
          <IconButton
            size='small'
            onClick={() => setEditReferral(params?.data)}
          >
            <EditIcon fontSize='small' className={classes?.editIcon} />
          </IconButton>
        </Tooltip>
      ),
    },
  ]

  const closeDialog = () => {
    setOpenDialog(false)
    response()
  }

  const closeEditDialog = () => {
    setEditReferral(null)
    response()
  }

  const handleSearch = (key: string, value: string) => {
    const keyValue =
      REFERRAL_SEARCH_OPTIONS?.find((e: any) => e?.label === key)?.value ??
      emptyString
    setFilterKey({
      key: keyValue,
      value: value,
    })
    setPage(0) // Reset to first page on new search
  }

  const triggerUploadClick = () => {
    fileInputRef.current?.click()
  }

  const handleFileChange = async (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const file = event.target?.files?.[0]
    if (!file) {
      return
    }

    const fileName = file.name.toLowerCase()
    if (!fileName.endsWith('.xlsx') && !fileName.endsWith('.xls')) {
      toast.error(TOAST_UPLOAD_INVALID_FILE)
      event.target.value = ''
      return
    }
    setUploadingFile(true)
    event.target.value = ''
    const uploadResult = await uploadReferralDetails(file)
    if (!uploadResult?.success) {
      toast.error(uploadResult?.message || TOAST_UPLOAD_FAILED)
      setUploadingFile(false)
      return
    }
    toast.success(uploadResult?.message || TOAST_UPLOAD_SUCCESS_FALLBACK)
    response()
    setUploadingFile(false)
  }

  return (
    <>
      <Box
        height={'100%'}
        width={'100%'}
        padding={2}
        display={'flex'}
        flexDirection={'column'}
        gap={0.5}
      >
        <Box
          display={'flex'}
          alignItems={'center'}
          justifyContent={'space-between'}
        >
          <Box>{REFERRAL_DETAILS_TITLE}</Box>
          <Box display={'flex'} alignItems={'center'} gap={1}>
            <Box>
              <GlobalSearch
                items={REFERRAL_SEARCH_OPTIONS}
                onSearch={handleSearch}
              />
            </Box>
            <Button
              variant='outlined'
              size='small'
              color='primary'
              className={classes?.exportButton}
              onClick={triggerUploadClick}
              disabled={uploadingFile}
            >
              {BTN_UPLOAD}
            </Button>
            <input
              ref={fileInputRef}
              type='file'
              accept='.xlsx,.xls'
              className={classes?.hiddenFileInput}
              onChange={handleFileChange}
            />
            <Button
              variant='outlined'
              size='small'
              color='primary'
              className={classes?.exportButton}
              startIcon={<GetAppOutlinedIcon fontSize='small' />}
              onClick={() => setOpenExportDialog(true)}
            >
              Export
            </Button>
            <ReferralExportDialog
              open={openExportDialog}
              onClose={() => setOpenExportDialog(false)}
              locationOptions={locationOptions}
            />
            <Button
              variant='contained'
              size='small'
              color='primary'
              className={classes?.addReferralButton}
              onClick={() => setOpenDialog(true)}
            >
              {ADD_REFERRAL}
            </Button>
            <ReferralForm open={openDialog} onClose={closeDialog} />
            <ReferralForm
              open={editReferral !== null}
              onClose={closeEditDialog}
              referral={editReferral}
            />
          </Box>
        </Box>
        <MedAgDataGrid
          tableHeight={'100%'}
          rowData={referralDetails}
          columnDefs={columnDefs}
          pagination={true}
          pageSizeOptions={pageSizeOptions}
          exportIcon={false}
          searchField={false}
          headerHeight={30}
          page={page}
          setPage={setPage}
          pageSize={pageSize}
          setPageSize={setPageSize}
          totalRows={totalCount}
          localSearch={false}
          enableCellTextSelection={true}
          loading={status === loading ? true : false}
        />
      </Box>
    </>
  )
}

export default ReferralDetails
